import React, { useState, useEffect } from 'react';
import { PONGFOX_FIX_URL_DRILL_V1, PONGFOX_FIX_URL_DRILL_SEMIRANDOM, BACKEND_API_URL_MYDRILLS_DEVICE } from "../constants/constants";
import { toast } from 'react-toastify';
import { Plus, Save, Trash2, Book, Shuffle, User } from 'lucide-react';
import './CreateSessionNewUI.css';

function DrillSelectionPopup({ deviceId, selectedDrills, setSelectedDrills, onClose, onDeleteDrill, dataV1, dataSemiRandom, dataCustomDrills, drills }) {
  const [activeApi, setActiveApi] = useState('v1');
  const [selectedVariation, setSelectedVariation] = useState('All');
  const [preSelectedStrokes, setPreSelectedStrokes] = useState({});
  const max_drills = 150;
  const drillCounts = selectedDrills.reduce((acc, drill) => {
    acc[drill.id] = (acc[drill.id] || 0) + 1;
    return acc;
  }, {});

  useEffect(() => {
    const initialStrokes = {};
    if (drills) {
      drills.forEach(drill => {
        if (drill.strokes && drill.strokes.length > 0) {
          initialStrokes[drill.id] = drill.strokes[0];
        }
      });
    }
    setPreSelectedStrokes(initialStrokes);
  }, [drills]);


  const handlePreSelectStroke = (drillId, stroke) => {
    setPreSelectedStrokes(prev => ({ ...prev, [drillId]: stroke }));
  };

  const handleAddDrill = (drill) => {
    if (selectedDrills.length >= max_drills) {
      toast.warn('You can only add up to '+ max_drills +' drills in a session.');
      return;
    }
    const count = selectedDrills.filter(item => item.title === drill.title).length;
    if (count >= 3) {
      toast.warn(`You can only add the same drill up to 3 times.`);
      return;
    }

    let checkDrill = 1, checkRandom = 0;
    let drillType = "";

    if (drill.apiType === "v1") {
      checkDrill = 1;
    }
    if (drill.isMixed === 1) {
      drillType = "isMixed";
    } else if (drill.apiType === "semirandom") {
      checkRandom = 1;
    }
    if (checkRandom) {
      drillType = "random";
    }
    if (drill.apiType === "custom_drills") {
      drillType = "isDirect";
    }
    if (drill.isMixedlevel) {
      drillType = "MixedLevel";
    }

    const newInstance = {
      ...drill,
      instanceId: `inst_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      targetLocation: '',
      numberOfBalls: 15,
      selectedStroke: preSelectedStrokes[drill.id] || (drill.strokes && drill.strokes.length > 0 ? drill.strokes[0] : null),
      isDrill: checkDrill,
      isRandom: checkRandom,
      drillType: drillType,
      repeat: 1,
      feedrate: drill.feedrate || 1.0,
      variations: drill.variations || 1,
      videoId: drill.videoId || "",
      random: drill.random || "",
      randomConfig: drill.randomConfig || "",
      tags: drill.tags || "",
      mirror: drill.mirror || "",
      pause: drill.pause || 0,
      player_level: drill.player_level || "",
    };
    setSelectedDrills(prev => [...prev, newInstance]);
  };

  const handleRemoveOneDrillInstance = (drillId) => {
    const indexToRemove = selectedDrills.findLastIndex(d => d.id === drillId);
    if (indexToRemove > -1) {
      setSelectedDrills(prev => prev.filter((_, index) => index !== indexToRemove));
    }
  };

  const getFilteredDrills = () => {
    const source = activeApi === 'v1' ? dataV1 : activeApi === 'semirandom' ? dataSemiRandom : dataCustomDrills;
    if (selectedVariation === 'All') {
      return source;
    }
    return source.filter(d => d.variations === selectedVariation);
  };

  const getTabIcon = (apiType) => {
    switch (apiType) {
      case 'v1': return <Book size={16} />;
      case 'semirandom': return <Shuffle size={16} />;
      case 'custom_drills': return <User size={16} />;
      default: return null;
    }
  };

  const getTabLabel = (apiType) => {
    switch (apiType) {
      case 'v1': return 'Library';
      case 'semirandom': return 'Semi Random';
      case 'custom_drills': return 'My Drills';
      default: return '';
    }
  };

  return (
    <div className="popup-backdrop" onClick={onClose}>
      <div className="drills-popup" onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", background: "var(--selected)", borderTopLeftRadius: "18px", borderTopRightRadius: "18px" }}>
          <h3 style={{ margin: 0, fontSize: "15px", fontWeight: "900", letterSpacing: ".4px", textTransform: "uppercase" }}>Add Drills</h3>
          <button className="btn ghost" onClick={onClose}>Done</button>
        </div>

        <div style={{ padding: "12px" }}>
          <label htmlFor="variation" className="strong-label">Select Variation</label>
          <select id="variation" value={selectedVariation} onChange={e => setSelectedVariation(parseInt(e.target.value) || 'All')}>
            <option value="All">All</option>
            <option value="1">Variation 1</option>
            <option value="2">Variation 2</option>
            <option value="3">Variation 3</option>
            <option value="4">Variation 4</option>
            <option value="5">Variation 5</option>
          </select>

          {/* Mobile-optimized tabs with icons and shorter labels */}
          <div className="tabs mobile-tabs" role="tablist" aria-label="Drill sources" style={{ marginTop: "12px" }}>
            <button
              className="tab mobile-tab"
              role="tab"
              aria-selected={activeApi === 'v1'}
              onClick={() => setActiveApi('v1')}
            >
              {getTabIcon('v1')}
              <span>{getTabLabel('v1')}</span>
            </button>
            <button
              className="tab mobile-tab"
              role="tab"
              aria-selected={activeApi === 'semirandom'}
              onClick={() => setActiveApi('semirandom')}
            >
              {getTabIcon('semirandom')}
              <span>{getTabLabel('semirandom')}</span>
            </button>
            <button
              className="tab mobile-tab"
              role="tab"
              aria-selected={activeApi === 'custom_drills'}
              onClick={() => setActiveApi('custom_drills')}
            >
              {getTabIcon('custom_drills')}
              <span>{getTabLabel('custom_drills')}</span>
            </button>
          </div>
          Limit: ({selectedDrills.length} / {max_drills})
          <div id="drillList" className="list" aria-live="polite" style={{ marginTop: "8px", maxHeight: "calc(80vh - 240px)", overflowY: "auto", paddingBottom: "50px" }}>
            {getFilteredDrills().map(drill => (
              <div className="row" key={drill.id}>
                <div className="meta">
                  <div className="r-title">{drill.title}</div>
                  <div className="r-desc">{drill.desc}</div>
                  <div className="pillset" aria-label="strokes" style={{display: 'flex', flexWrap: 'nowrap', overflowX: 'auto'}}>
                    {drill.strokes.map(s =>
                      <button
                        key={s}
                        className="pill"
                        aria-selected={preSelectedStrokes[drill.id] === s}
                        onClick={() => handlePreSelectStroke(drill.id, s)}>
                        {s}
                      </button>)}
                  </div>
                </div>
                <div className="qty" data-id={drill.id} style={{ display: 'flex', alignItems: 'center' }}>
                  <button type="button" aria-label="decrease" onClick={() => handleRemoveOneDrillInstance(drill.id)}>−</button>
                  <input type="text" inputMode="numeric" value={drillCounts[drill.id] || 0} aria-label="count" readOnly />
                  <button type="button" aria-label="increase" onClick={() => handleAddDrill(drill)}>+</button>
                </div>
                {/* Custom drill delete button */}
                {drill.apiType === 'custom_drills' && (
                  <button className="btn danger" style={{ padding: '5px', marginLeft: '5px' }} onClick={(e) => { e.stopPropagation(); onDeleteDrill(drill.drill_id); }}>
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DrillSelectionPopup;